'use client';

import { createContext, useContext, useState, ReactNode } from 'react';
import type { MidjenRouteTracePolicy } from '@/app/types/analytics';

/**
 * Page-level trace policy identifier for navigation and initial load events.
 *
 * IMPORTANT: This is ONLY for page-level trace policies (navigation and cold starts).
 * Interaction-level trace policies (e.g., button clicks) should NOT use this context.
 */
export type PageTracePolicy = MidjenRouteTracePolicy | string;

interface TracePolicyContextValue {
  /**
   * Current page-level trace policy identifier for the active route
   */
  tracePolicy: PageTracePolicy | null;

  /**
   * Set the page-level trace policy for the current route
   *
   * IMPORTANT: Only use this for page navigation tracking.
   * Do NOT use for interaction-level events.
   */
  setTracePolicy: (policy: PageTracePolicy) => void;
}

const TracePolicyContext = createContext<TracePolicyContextValue | undefined>(undefined);

interface TracePolicyProviderProps {
  children: ReactNode;
  initialTracePolicy?: PageTracePolicy;
}

/**
 * Provider component for page-level trace policy tracking.
 *
 * Mount this at the root of your application to enable trace policy tracking.
 *
 * @example
 * ```tsx
 * function RootLayout({ children }) {
 *   return (
 *     <TracePolicyProvider>
 *       {children}
 *     </TracePolicyProvider>
 *   );
 * }
 * ```
 */
export function TracePolicyProvider({ children, initialTracePolicy }: TracePolicyProviderProps) {
  const [tracePolicy, setTracePolicyState] = useState<PageTracePolicy | null>(
    initialTracePolicy ?? null
  );

  const setTracePolicy = (policy: PageTracePolicy) => {
    setTracePolicyState(policy);
  };

  return (
    <TracePolicyContext.Provider value={{ tracePolicy, setTracePolicy }}>
      {children}
    </TracePolicyContext.Provider>
  );
}

/**
 * Hook to access the current page-level trace policy
 *
 * IMPORTANT: This returns the page-level trace policy only (for navigation tracking).
 * Do NOT use this for interaction-level trace policies.
 *
 * @returns The current page trace policy identifier, or null if not set
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const tracePolicy = usePageTracePolicy();
 *   console.log('Current page:', tracePolicy); // "midjen.home"
 * }
 * ```
 */
export function usePageTracePolicy(): PageTracePolicy | null {
  const context = useContext(TracePolicyContext);

  if (context === undefined) {
    throw new Error('usePageTracePolicy must be used within a TracePolicyProvider');
  }

  return context.tracePolicy;
}

/**
 * Hook to set the current page-level trace policy
 *
 * IMPORTANT: Only use this for page navigation tracking.
 *
 * @returns A function to set the trace policy
 *
 * @example
 * ```tsx
 * function MyPage() {
 *   const setTracePolicy = useSetPageTracePolicy();
 *
 *   useEffect(() => {
 *     setTracePolicy(MidjenRouteTracePolicy.HOME);
 *   }, []);
 * }
 * ```
 */
export function useSetPageTracePolicy(): (policy: PageTracePolicy) => void {
  const context = useContext(TracePolicyContext);

  if (context === undefined) {
    throw new Error('useSetPageTracePolicy must be used within a TracePolicyProvider');
  }

  return context.setTracePolicy;
}

export default TracePolicyContext;
